import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fmtMoney, fmtDate, todayISO } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ArrowRight } from "lucide-react";

export default function WeeklySummary() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState({ start: "", end: "" });

  useEffect(() => {
    (async () => {
      // Fetch the company's `work_week_start_day` (0=Mon..6=Sun) so the
      // Weekly Summary uses the SAME week window as the paystub bucket.
      // Previously this was hardcoded to Monday-start, which caused the
      // "hours here don't match my paystub" complaint when a tenant runs
      // a Thursday-start pay week.
      let startDay = 0; // Monday default
      try {
        const s = await api.get("/settings");
        const raw = s.data?.work_week_start_day;
        if (raw !== null && raw !== undefined) startDay = Number(raw);
      } catch { /* keep default */ }

      const today = new Date(todayISO() + "T00:00:00");
      // JS getDay(): 0=Sun..6=Sat. Convert to app convention 0=Mon..6=Sun.
      const appDow = (today.getDay() + 6) % 7;
      const backToStart = (appDow - startDay + 7) % 7;
      const weekStart = new Date(today);
      weekStart.setDate(today.getDate() - backToStart);
      const weekEnd = new Date(weekStart);
      weekEnd.setDate(weekStart.getDate() + 6);

      const startISO = weekStart.toISOString().slice(0, 10);
      const endISO = weekEnd.toISOString().slice(0, 10);
      setRange({ start: startISO, end: endISO });

      const { data } = await api.get("/daily/submissions", {
        params: {
          date_from: startISO,
          date_to: endISO,
          include_unfinalized: true,
        },
      });
      setItems(data);
      setLoading(false);
    })();
    // Intentional mount-only fetch. Every reference inside is a stable
    // module import (api, todayISO) or a locally-scoped constant — adding
    // them to the dep array would refetch on every render and reset the
    // page state. The linter warning is a false positive here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const totals = items.reduce(
    (acc, s) => ({
      hours: acc.hours + (s.hours?.net_hours || 0),
      gross: acc.gross + (s.production?.gross_invoice_total || 0),
      net: acc.net + (s.production?.net_commissionable_revenue || 0),
      pay: acc.pay + (s.pay?.estimated_final_pay || 0),
    }),
    { hours: 0, gross: 0, net: 0, pay: 0 }
  );

  const unfinalizedItems = items.filter((s) => s.status === "unfinalized");

  if (loading) return <div className="py-20 text-center text-slate-500">Loading…</div>;

  return (
    <div className="space-y-5" data-testid="weekly-page">
      <header>
        <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">This Week</p>
        <h1 className="text-3xl font-extrabold font-display tracking-tighter text-slate-900 mt-1">Weekly Summary</h1>
        {range.start && (
          <p className="text-sm text-slate-500 mt-1" data-testid="weekly-range">
            {fmtDate(range.start)} – {fmtDate(range.end)}
          </p>
        )}
      </header>

      {unfinalizedItems.length > 0 && (
        <Card className="rounded-2xl border-amber-300 bg-amber-50" data-testid="weekly-unfinalized-banner">
          <CardContent className="p-4 flex items-start gap-3">
            <div className="h-9 w-9 rounded-full bg-amber-100 flex items-center justify-center shrink-0">
              <AlertTriangle className="h-5 w-5 text-amber-700" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-amber-900">
                {unfinalizedItems.length} day{unfinalizedItems.length === 1 ? "" : "s"} not finalized yet
              </p>
              <p className="text-xs text-amber-800 mt-0.5">
                Your hours still count for payroll, but you should finalize each day to lock them in. Tap a yellow row below to finalize.
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 gap-3">
        <Stat label="Net Hours" value={totals.hours.toFixed(2)} />
        <Stat label="Est. Pay" value={fmtMoney(totals.pay)} accent />
        <Stat label="Gross Production" value={fmtMoney(totals.gross)} />
        <Stat label="Net Commissionable" value={fmtMoney(totals.net)} />
      </div>

      {items.length === 0 ? (
        <Card className="rounded-2xl border-dashed border-2 border-slate-300">
          <CardContent className="py-10 text-center text-slate-500">No submissions this week.</CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {items.map((s) => {
            const isUnfinalized = s.status === "unfinalized";
            const isEditable = ["submitted", "pending_approval", "approved", "rejected"].includes(s.status);
            const rowKey = s.id || `unf-${s.date}`;
            const Wrap = isUnfinalized ? Link : (isEditable ? Link : "div");
            const wrapProps = isUnfinalized
              ? { to: `/finalize?date=${s.date}`, "data-testid": `week-finalize-link-${s.date}` }
              : (isEditable
                ? { to: `/week/edit/${s.id}`, "data-testid": `week-edit-link-${s.date}` }
                : {});
            return (
              <Wrap key={rowKey} {...wrapProps} className={(isUnfinalized || isEditable) ? "block" : undefined}>
                <Card
                  className={`rounded-2xl border-slate-200 ${isUnfinalized ? "border-amber-300 bg-amber-50 hover:bg-amber-100 transition cursor-pointer" : ""} ${isEditable ? "hover:bg-slate-50 transition cursor-pointer" : ""}`}
                  data-testid={`week-row-${s.date}`}
                >
                  <CardContent className="p-4 flex items-center justify-between gap-3">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-900">{fmtDate(s.date)}</p>
                      <p className="text-xs text-slate-500">{s.hours?.net_hours?.toFixed(2)}h · {fmtMoney(s.production?.gross_invoice_total)} prod</p>
                    </div>
                    <div className="text-right shrink-0">
                      {isUnfinalized ? (
                        <span className="inline-flex items-center gap-1 text-amber-900 font-bold text-sm" data-testid={`tap-to-finalize-${s.date}`}>
                          TAP TO FINALIZE <ArrowRight className="h-4 w-4" />
                        </span>
                      ) : (
                        <p className="text-lg font-extrabold text-green-700">{fmtMoney(s.pay?.estimated_final_pay)}</p>
                      )}
                      <div className="mt-0.5 flex items-center justify-end gap-1">
                        <StatusBadge status={s.status} />
                        {isEditable && <ArrowRight className="h-3.5 w-3.5 text-slate-400" />}
                      </div>
                    </div>
                  </CardContent>
                  {/* Admin action callout: reopen reason or rejection note.
                      Shows on days the tech needs to look at. */}
                  {(s.reopened_reason || (s.status === "rejected" && s.admin_notes)) && (
                    <div
                      className="px-4 pb-3 -mt-1"
                      data-testid={`week-admin-note-${s.date}`}
                    >
                      <div className={`rounded-lg border p-2.5 text-xs flex items-start gap-2 ${
                        s.reopened_reason
                          ? "bg-sky-50 border-sky-200 text-sky-900"
                          : "bg-red-50 border-red-200 text-red-900"
                      }`}>
                        <span className="text-sm leading-none mt-0.5">{s.reopened_reason ? "🔄" : "🚫"}</span>
                        <div className="min-w-0">
                          <p className="font-bold">
                            {s.reopened_reason
                              ? `Reopened by ${s.reopened_by_name || "admin"} — please fix`
                              : `Rejected by admin — please fix`}
                          </p>
                          <p className="mt-0.5 whitespace-pre-wrap break-words">
                            {s.reopened_reason || s.admin_notes}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </Card>
              </Wrap>
            );
          })}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, accent }) {
  return (
    <Card className="rounded-2xl border-slate-200">
      <CardContent className="p-4">
        <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</p>
        <p className={`text-2xl font-extrabold font-display mt-1 ${accent ? "text-green-700" : "text-slate-900"}`}>{value}</p>
      </CardContent>
    </Card>
  );
}

export function StatusBadge({ status }) {
  const map = {
    draft: "bg-slate-100 text-slate-700",
    submitted: "bg-sky-100 text-sky-700",
    pending_approval: "bg-amber-100 text-amber-800",
    approved: "bg-green-100 text-green-700",
    rejected: "bg-red-100 text-red-700",
    locked: "bg-slate-900 text-white",
    no_submission: "bg-slate-200 text-slate-700",
    clocked_out_no_submit: "bg-amber-100 text-amber-800",
    working: "bg-green-100 text-green-700",
    no_activity: "bg-slate-100 text-slate-500",
    unfinalized: "bg-amber-200 text-amber-900",
  };
  const label = (status || "").replace(/_/g, " ");
  return <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${map[status] || map.draft}`}>{label}</span>;
}
